import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Article, Add, Edit, Delete, Visibility, VisibilityOff, 
  Save, Cancel, Description, ArrowBack
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { cmsService, CMSPage, BlogPost, FAQItem } from '../services/cms';
import FAQEditor from '../components/FAQEditor';

type TabType = 'pages' | 'blog' | 'faq';

const AdminCMSPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('pages');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // CMS Pages state
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [editingPage, setEditingPage] = useState<CMSPage | null>(null);
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  // Blog state
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isCreatingPost, setIsCreatingPost] = useState(false);

  // FAQ state
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [editingFaq, setEditingFaq] = useState<FAQItem | null>(null);
  const [isCreatingFaq, setIsCreatingFaq] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'pages') {
        const data = await cmsService.getAllPages(true);
        setPages(data);
      } else if (activeTab === 'blog') {
        const data = await cmsService.getAllBlogPosts(true);
        setBlogPosts(data);
      } else if (activeTab === 'faq') {
        const data = await cmsService.getAllFAQItems(true);
        setFaqItems(data);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = async (pageId: number) => {
    if (!window.confirm(language === 'tr' ? 'Silmek istediğinize emin misiniz?' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      await cmsService.deletePage(pageId);
      setSuccess(language === 'tr' ? 'Sayfa silindi' : 'Page deleted');
      loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete page');
    }
  };

  const handleDeletePost = async (postId: number) => {
    if (!window.confirm(language === 'tr' ? 'Silmek istediğinize emin misiniz?' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      await cmsService.deleteBlogPost(postId);
      setSuccess(language === 'tr' ? 'Blog yazısı silindi' : 'Blog post deleted');
      loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleDeleteFaq = async (faqId: number) => {
    if (!window.confirm(language === 'tr' ? 'Silmek istediğinize emin misiniz?' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      await cmsService.deleteFAQItem(faqId);
      setSuccess(language === 'tr' ? 'SSS silindi' : 'FAQ deleted');
      loadData();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete FAQ');
    }
  };

  const handleSaveFaq = async (data: any) => {
    try {
      if (isCreatingFaq) {
        await cmsService.createFAQItem(data);
        setSuccess(language === 'tr' ? 'SSS oluşturuldu' : 'FAQ created');
      } else if (editingFaq) {
        await cmsService.updateFAQItem(editingFaq.id, data);
        setSuccess(language === 'tr' ? 'SSS güncellendi' : 'FAQ updated');
      }
      setEditingFaq(null);
      setIsCreatingFaq(false);
      loadData();
    } catch (err: any) {
      throw err; // Let editor handle the error
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowBack style={{ width: '20px', height: '20px' }} />
                <span>{language === 'tr' ? 'Admin Paneli' : 'Admin Panel'}</span>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <Article className="text-gray-900 dark:text-white" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {language === 'tr' ? 'İçerik Yönetimi' : 'Content Management'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('pages')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'pages'
                ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {language === 'tr' ? 'Sayfalar' : 'Pages'} ({pages.length})
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'blog'
                ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {language === 'tr' ? 'Blog Yazıları' : 'Blog Posts'} ({blogPosts.length})
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'faq'
                ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {language === 'tr' ? 'SSS' : 'FAQ'} ({faqItems.length})
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-700 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Add New Button */}
        <div className="mb-6">
          <button
            onClick={() => {
              if (activeTab === 'pages') setIsCreatingPage(true);
              else if (activeTab === 'blog') setIsCreatingPost(true);
              else if (activeTab === 'faq') setIsCreatingFaq(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            <Add style={{ width: '20px', height: '20px' }} />
            <span>
              {activeTab === 'pages' && (language === 'tr' ? 'Yeni Sayfa' : 'New Page')}
              {activeTab === 'blog' && (language === 'tr' ? 'Yeni Blog Yazısı' : 'New Blog Post')}
              {activeTab === 'faq' && (language === 'tr' ? 'Yeni SSS' : 'New FAQ')}
            </span>
          </button>
        </div>

        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
            </p>
          </div>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && !loading && (
          <div className="space-y-4">
            {pages.map((page) => (
              <div
                key={page.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'tr' ? page.title_tr : page.title_en}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Slug: {page.slug} | Type: {page.page_type}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      {page.is_published ? (
                        <span className="flex items-center space-x-1 text-green-600 dark:text-green-400 text-sm">
                          <Visibility style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Yayında' : 'Published'}</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 text-sm">
                          <VisibilityOff style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Taslak' : 'Draft'}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingPage(page)}
                      className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <Edit style={{ width: '20px', height: '20px' }} />
                    </button>
                    <button
                      onClick={() => handleDeletePage(page.id)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Delete style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && !loading && (
          <div className="space-y-4">
            {blogPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'tr' ? post.title_tr : post.title_en}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Slug: {post.slug} | Views: {post.view_count} | Author: {post.author}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      {post.is_published ? (
                        <span className="flex items-center space-x-1 text-green-600 dark:text-green-400 text-sm">
                          <Visibility style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Yayında' : 'Published'}</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 text-sm">
                          <VisibilityOff style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Taslak' : 'Draft'}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingPost(post)}
                      className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <Edit style={{ width: '20px', height: '20px' }} />
                    </button>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Delete style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && !loading && (
          <div className="space-y-4">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'tr' ? faq.question_tr : faq.question_en}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Category: {faq.category} | Order: {faq.display_order}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      {faq.is_published ? (
                        <span className="flex items-center space-x-1 text-green-600 dark:text-green-400 text-sm">
                          <Visibility style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Yayında' : 'Published'}</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 text-sm">
                          <VisibilityOff style={{ width: '16px', height: '16px' }} />
                          <span>{language === 'tr' ? 'Taslak' : 'Draft'}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingFaq(faq)}
                      className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <Edit style={{ width: '20px', height: '20px' }} />
                    </button>
                    <button
                      onClick={() => handleDeleteFaq(faq.id)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Delete style={{ width: '20px', height: '20px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Note: Edit modals will be added in next iteration */}
      {(editingPage || isCreatingPage) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <p className="text-gray-900 dark:text-white">
              {language === 'tr' ? 'Sayfa düzenleme özelliği ekleniyor...' : 'Page editor feature coming soon...'}
            </p>
            <button
              onClick={() => {
                setEditingPage(null);
                setIsCreatingPage(false);
              }}
              className="mt-4 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg"
            >
              {language === 'tr' ? 'Kapat' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {(editingPost || isCreatingPost) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <p className="text-gray-900 dark:text-white">
              {language === 'tr' ? 'Blog düzenleme özelliği ekleniyor...' : 'Blog editor feature coming soon...'}
            </p>
            <button
              onClick={() => {
                setEditingPost(null);
                setIsCreatingPost(false);
              }}
              className="mt-4 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg"
            >
              {language === 'tr' ? 'Kapat' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {(editingFaq || isCreatingFaq) && (
        <FAQEditor
          faq={editingFaq}
          isCreating={isCreatingFaq}
          onSave={handleSaveFaq}
          onCancel={() => {
            setEditingFaq(null);
            setIsCreatingFaq(false);
          }}
          language={language}
        />
      )}
    </div>
  );
};

export default AdminCMSPage;

